import type { JSX } from 'react';
import { cn } from '@/lib/utils';
import { monoLabel } from '../shell/look';
import { PAGE_SIZE, pageCount, pageWindow } from './activity-query';

const cell =
  'grid h-8 min-w-8 place-items-center rounded-[8px] border px-2 font-mono text-[12px] tabular-nums transition-colors disabled:opacity-40';

/** "51–100 of 342", then ‹ 1 … 4 5 6 … 12 ›. Each click changes `?page=` in the URL. */
export function ActivityPager({
  page,
  total,
  onPage,
}: {
  page: number;
  total: number;
  onPage: (page: number) => void;
}): JSX.Element | null {
  const pages = pageCount(total);
  if (total === 0) return null;
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE);

  return (
    <nav
      aria-label="Activity pages"
      className="mt-4 flex flex-wrap items-center justify-between gap-3"
    >
      <div className={cn(monoLabel, 'text-mute text-[11px]')}>
        {from}–{to} of {total.toLocaleString()}
      </div>
      {pages > 1 && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous page"
            className={cn(cell, 'border-line text-ink hover:bg-surf2')}
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
          >
            ‹
          </button>
          {pageWindow(page, pages).map((p, i) =>
            p === null ? (
              <span key={`gap-${i}`} className="text-mute px-1 font-mono text-[12px]">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                aria-current={p === page ? 'page' : undefined}
                className={cn(
                  cell,
                  p === page
                    ? 'border-acc bg-acc-soft text-acc'
                    : 'border-line text-ink hover:bg-surf2',
                )}
                onClick={() => onPage(p)}
              >
                {p}
              </button>
            ),
          )}
          <button
            type="button"
            aria-label="Next page"
            className={cn(cell, 'border-line text-ink hover:bg-surf2')}
            disabled={page >= pages}
            onClick={() => onPage(page + 1)}
          >
            ›
          </button>
        </div>
      )}
    </nav>
  );
}
